import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useTranslation } from "react-i18next";

import { AdminCreditsAPI } from "../../api/adminCreditsApi";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

function uniq(arr) {
  return Array.from(new Set(arr));
}

export default function AdminLabTagsPage() {
  const { t } = useTranslation();

  const [labs, setLabs] = useState([]);
  const [allTags, setAllTags] = useState([]);

  const [q, setQ] = useState("");
  const [baseLoading, setBaseLoading] = useState(false);
  const [error, setError] = useState("");

  // cache tags par lab
  const [labTagsCache, setLabTagsCache] = useState({}); // { [labId]: ["CCNA"] }
  const [labTagsLoading, setLabTagsLoading] = useState({}); // { [labId]: true }

  // grid pagination
  const [pageSize, setPageSize] = useState(10);

  // snack
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const showSnack = (severity, message) => {
    setSnackSeverity(severity);
    setSnackMsg(message);
    setSnackOpen(true);
  };

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editLab, setEditLab] = useState(null);
  const [editSelected, setEditSelected] = useState([]);

  // duplicate dialog
  const [dupOpen, setDupOpen] = useState(false);
  const [dupSourceLab, setDupSourceLab] = useState(null);
  const [dupTargetLab, setDupTargetLab] = useState(null);

  const tagOptions = useMemo(() => {
    return (allTags ?? [])
      .map((t) => ({ id: t.id, code: t.code, name: t.name }))
      .sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [allTags]);

  const filteredLabs = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return labs;
    return labs.filter(
      (l) =>
        String(l.name ?? "").toLowerCase().includes(s) ||
        String(l.id ?? "").includes(s)
    );
  }, [labs, q]);

  const loadBase = async () => {
    setError("");
    setBaseLoading(true);
    try {
      const [labsRes, tagsRes] = await Promise.all([
        AdminCreditsAPI.listLabs(),
        AdminCreditsAPI.listTags(),
      ]);

      const labsPayload = labsRes?.data ?? labsRes;
      const tagsPayload = tagsRes?.data ?? tagsRes;

      const labsList = labsPayload?.items ?? labsPayload?.data ?? labsPayload ?? [];
      const tagsList = tagsPayload?.items ?? tagsPayload?.tags ?? tagsPayload?.data ?? tagsPayload ?? [];

      setLabs(Array.isArray(labsList) ? labsList : []);
      setAllTags(Array.isArray(tagsList) ? tagsList : []);
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setBaseLoading(false);
    }
  };

  const getLabTags = async (labId) => {
    if (labTagsCache[labId] !== undefined) return labTagsCache[labId];

    setLabTagsLoading((p) => ({ ...p, [labId]: true }));
    try {
      const r = await AdminCreditsAPI.getLabTags(labId);
      const payload = r?.data ?? r;
      const list = payload?.tags ?? payload?.items ?? payload ?? [];
      const codes = Array.isArray(list) ? list : [];
      setLabTagsCache((p) => ({ ...p, [labId]: codes }));
      return codes;
    } catch (e) {
      const msg = extractErrorMessage(e);
      showSnack("error", msg);
      setLabTagsCache((p) => ({ ...p, [labId]: [] }));
      return [];
    } finally {
      setLabTagsLoading((p) => ({ ...p, [labId]: false }));
    }
  };

  // Lazy load: précharger les tags des labs visibles
  const prefetchVisible = async (visibleLabIds) => {
    const missing = visibleLabIds.filter(
      (id) => labTagsCache[id] === undefined && !labTagsLoading[id]
    );
    if (!missing.length) return;
    await Promise.all(missing.map((id) => getLabTags(id)));
  };

  useEffect(() => {
    loadBase();
  }, []);

  const openEdit = async (lab) => {
    setEditLab(lab);
    setEditSelected([]);
    setEditOpen(true);

    const codes = await getLabTags(lab.id);
    const selectedObjects = tagOptions.filter((t) => codes.includes(t.code));
    setEditSelected(selectedObjects);
  };

  const saveEdit = async () => {
    if (!editLab) return;
    try {
      const codes = uniq(editSelected.map((t) => t.code));
      await AdminCreditsAPI.setLabTags(editLab.id, { tags: codes });
      setLabTagsCache((p) => ({ ...p, [editLab.id]: codes }));
      setEditOpen(false);
      showSnack("success", t("admin.labTags.snack.updated", { id: editLab.id }));
    } catch (e) {
      showSnack("error", extractErrorMessage(e));
    }
  };

  const clearTags = async (lab) => {
    const ok = window.confirm(t("admin.labTags.confirm.clear", { id: lab.id, name: lab.name }));
    if (!ok) return;

    try {
      await AdminCreditsAPI.setLabTags(lab.id, { tags: [] });
      setLabTagsCache((p) => ({ ...p, [lab.id]: [] }));
      showSnack("success", t("admin.labTags.snack.cleared", { id: lab.id }));
    } catch (e) {
      showSnack("error", extractErrorMessage(e));
    }
  };

  const openDuplicate = async (lab) => {
    setDupSourceLab(lab);
    setDupTargetLab(null);
    setDupOpen(true);
    await getLabTags(lab.id);
  };

  const saveDuplicate = async () => {
    if (!dupSourceLab || !dupTargetLab) return;
    if (dupSourceLab.id === dupTargetLab.id) return;

    try {
      const codes = await getLabTags(dupSourceLab.id);
      await AdminCreditsAPI.setLabTags(dupTargetLab.id, { tags: codes });
      setLabTagsCache((p) => ({ ...p, [dupTargetLab.id]: codes }));
      setDupOpen(false);
      showSnack("success", t("admin.labTags.snack.duplicated", { from: dupSourceLab.id, to: dupTargetLab.id }));
    } catch (e) {
      showSnack("error", extractErrorMessage(e));
    }
  };

  const renderTagsCell = (labId) => {
    const isLoading = Boolean(labTagsLoading[labId]);
    const codes = labTagsCache[labId];

    if (isLoading || codes === undefined) {
      return (
        <Stack direction="row" gap={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {t("common.loading")}
          </Typography>
        </Stack>
      );
    }

    if (!codes.length) {
      return <Typography variant="body2" sx={{ opacity: 0.6 }}>{t("admin.labTags.noTags")}</Typography>;
    }

    const preview = codes.slice(0, 4);
    const rest = codes.length - preview.length;

    return (
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {preview.map((c) => (
          <Chip key={c} label={c} size="small" icon={<LocalOfferOutlinedIcon />} />
        ))}
        {rest > 0 && (
          <Tooltip title={codes.join(", ")}>
            <Chip label={`+${rest}`} size="small" variant="outlined" />
          </Tooltip>
        )}
      </Stack>
    );
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: t("admin.labTags.columns.id"), width: 90 },
      {
        field: "name",
        headerName: t("admin.labTags.columns.lab"),
        flex: 1,
        minWidth: 220,
        renderCell: (params) => (
          <Box>
            <Typography fontWeight={900}>{params.row.name}</Typography>
            {params.row.description ? (
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                {params.row.description}
              </Typography>
            ) : null}
          </Box>
        ),
      },
      {
        field: "tags",
        headerName: t("admin.labTags.columns.tags"),
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => renderTagsCell(params.row.id),
      },
      {
        field: "actions",
        headerName: t("admin.labTags.columns.actions"),
        width: 190,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const lab = params.row;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t("common.edit")}>
                <span>
                  <IconButton size="small" onClick={() => openEdit(lab)} disabled={baseLoading}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("common.duplicate")}>
                <span>
                  <IconButton size="small" onClick={() => openDuplicate(lab)} disabled={baseLoading}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("admin.labTags.actions.clear")}>
                <span>
                  <IconButton size="small" onClick={() => clearTags(lab)} disabled={baseLoading} color="error">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [t, baseLoading, labTagsCache, labTagsLoading]
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            {t("admin.labTags.title")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {t("admin.labTags.subtitle")}
          </Typography>
        </Box>

        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            size="small"
            label={t("common.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.labTags.searchPlaceholder")}
          />
          <Tooltip title={t("common.refresh")}>
            <span>
              <IconButton onClick={loadBase} disabled={baseLoading}>
                <RefreshOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={filteredLabs}
          columns={columns}
          getRowId={(r) => r.id}
          loading={baseLoading}
          pageSizeOptions={[5, 10, 20, 50]}
          paginationModel={{ pageSize, page: 0 }}
          onPaginationModelChange={(m) => setPageSize(m.pageSize)}
          disableRowSelectionOnClick
          onStateChange={(state) => {
            // lazy load sur lignes visibles
            const visibleSorted = state?.visibleRowsLookup
              ? Object.keys(state.visibleRowsLookup).filter((k) => state.visibleRowsLookup[k])
              : [];
            const ids = visibleSorted.map((x) => Number(x)).filter((n) => Number.isFinite(n));
            if (ids.length) prefetchVisible(ids);
          }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": { bgcolor: "rgba(0,0,0,0.02)" },
          }}
        />
      </Paper>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.labTags.dialog.editTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {t("admin.labTags.dialog.lab")} <b>#{editLab?.id}</b> — {editLab?.name}
            </Typography>

            <Autocomplete
              multiple
              options={tagOptions}
              value={editSelected}
              onChange={(e, v) => setEditSelected(v)}
              getOptionLabel={(o) => `${o.code} — ${o.name}`}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.labTags.fields.tags")} placeholder={t("admin.labTags.fields.tagsPlaceholder")} />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={saveEdit}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate dialog */}
      <Dialog open={dupOpen} onClose={() => setDupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.labTags.dialog.duplicateTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {t("admin.labTags.dialog.source")} <b>#{dupSourceLab?.id}</b> — {dupSourceLab?.name}
            </Typography>

            <Autocomplete
              options={labs}
              value={dupTargetLab}
              onChange={(e, v) => setDupTargetLab(v)}
              getOptionLabel={(l) => `#${l.id} — ${l.name}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.labTags.dialog.target")} placeholder={t("admin.labTags.dialog.targetPlaceholder")} />
              )}
            />

            <Alert severity="info">{t("admin.labTags.dialog.duplicateHint")}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveDuplicate}
            disabled={!dupTargetLab || dupTargetLab?.id === dupSourceLab?.id}
          >
            {t("common.duplicate")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
